import SaveIcon from "@mui/icons-material/Save"
import {
  Alert,
  Box,
  Button,
  DialogActions,
  Grid,
  Paper,
  Snackbar,
  TextField,
} from "@mui/material"
import dayjs from "dayjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import { doc, getDoc, getFirestore, setDoc } from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import Dimen from "../Dimen"
import { paperStyleDefault } from "../Style"
import { timber_debug } from "../Timber"

const BusinessProfile: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const intl = useIntl()

  // Firebase
  const firestore = getFirestore()
  const analytics = getAnalytics()
  const profileRef = doc(firestore, `/users/${userId}/private`, "profile")

  // useStates
  const [name, setName] = useState<string>("")
  const [addressLineOne, setAddressLineOne] = useState<string>("")
  const [addressLineTwo, setAddressLineTwo] = useState<string>("")
  const [phone, setPhone] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [website, setWebsite] = useState<string>("")
  const [snackBarMsg, setSnackBarMsg] = useState<string>("")

  useEffect(() => {
    getDoc(profileRef).then((docSnap) => {
      if (docSnap.exists()) {
        timber_debug("Downloaded profile snapshot")
        setName(docSnap.data().name)
        setAddressLineOne(docSnap.data().addressLineOne)
        setAddressLineTwo(docSnap.data().addressLineTwo)
        setPhone(docSnap.data().phone)
        setEmail(docSnap.data().email)
        setWebsite(docSnap.data().website)
      } else {
        timber_debug("No profile doc exists")
      }
    })
    return () => {}
  }, [])

  const saveProfile = () => {
    const currentTime = dayjs().valueOf()
    setDoc(profileRef, {
      name: name,
      addressLineOne: addressLineOne,
      addressLineTwo: addressLineTwo,
      phone: phone,
      email: email,
      website: website,
      insertDate: currentTime,
      lastEditDate: currentTime,
    })
      .then(() => {
        setSnackBarMsg(
          intl.formatMessage({
            id: "snackbar.save.successful",
          })
        )
        timber_debug("Profile saved successfully")
        logEvent(analytics, "web_profile_saved")
      })
      .catch((error: Error) => {
        console.error(error)
        setSnackBarMsg(
          intl.formatMessage({
            id: "snackbar.save.error",
          })
        )
        logEvent(analytics, "web_profile_save_error")
      })
  }

  const handleSnakbarClose = () => {
    setSnackBarMsg("")
  }

  return (
    <Box height="91vh" display="flex" flexDirection="column">
      <Paper
        elevation={3}
        style={{
          height: "85vh",
          margin: "8px",
          ...paperStyleDefault,
          padding: "8px",
        }}
      >
        <div style={{ fontSize: Dimen.FontSizeDefault }}>
          <FormattedMessage id="nav.business.profile" />
        </div>
        <Grid container spacing={2} direction={"column"} marginTop={1}>
          {/* Business name */}
          <Grid size={{ sm: 12, md: 12, lg: 6 }}>
            <TextField
              fullWidth
              required
              label={intl.formatMessage({
                id: "business.profile.name",
              })}
              value={name}
              onChange={(event: any) => {
                setName(event.target.value)
              }}
            />
          </Grid>

          {/* Address Line 1 */}
          <Grid size={{ sm: 12, md: 12, lg: 6 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({
                id: "business.profile.address.line.one",
              })}
              value={addressLineOne}
              onChange={(event: any) => {
                setAddressLineOne(event.target.value)
              }}
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid size={{ sm: 12, md: 12, lg: 6 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({
                id: "business.profile.address.line.two",
              })}
              value={addressLineTwo}
              onChange={(event: any) => {
                setAddressLineTwo(event.target.value)
              }}
            />
          </Grid>

          {/* Phone */}
          <Grid size={{ sm: 12, md: 12, lg: 6 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({
                id: "business.profile.phone",
              })}
              value={phone}
              onChange={(event: any) => {
                setPhone(event.target.value)
              }}
            />
          </Grid>

          {/* Email */}
          <Grid size={{ sm: 12, md: 12, lg: 6 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({
                id: "business.profile.email",
              })}
              value={email}
              onChange={(event: any) => {
                setEmail(event.target.value)
              }}
            />
          </Grid>

          {/* Website */}
          <Grid size={{ sm: 12, md: 12, lg: 6 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({
                id: "business.profile.website",
              })}
              value={website}
              onChange={(event: any) => {
                setWebsite(event.target.value)
              }}
            />
          </Grid>
        </Grid>
        {/* Dialog Action Buttons */}
        <DialogActions>
          <Button
            onClick={saveProfile}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            <FormattedMessage id="dialog.button.save" />
          </Button>
        </DialogActions>
      </Paper>
      <Snackbar
        open={snackBarMsg !== ""}
        autoHideDuration={4000}
        onClose={handleSnakbarClose}
      >
        <Alert
          variant="filled"
          severity="success"
          sx={{
            width: "100%",
            background: Color.PrimaryLight85,
            color: Color.Black,
          }}
        >
          {snackBarMsg}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default BusinessProfile
